import { Session, User } from '@supabase/supabase-js';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useToast } from '@/hooks/use-toast';

import { decryptApiKey, encryptApiKey } from '@/lib/crypto';
import { clearUserInfo, setUserInfo } from '@/lib/monitoring';
import { QuickLoggerFactory } from '@/lib/universal-logger';

import { supabase } from '@/integrations/supabase/client';

// Initialize auth namespace logger for authentication operations
const authLogger = QuickLoggerFactory.auth();

/**
 * Represents the structure of the user profile data fetched from the database.
 */
interface ProfileData {
  /** The unique identifier for the profile, matching the user ID. */
  id: string;
  /** The user's chosen username, if set. */
  username: string | null;
  /** The user's full name, if provided during signup or updated later. */
  full_name: string | null;
  /** URL to the user's avatar image, if set. */
  avatar_url: string | null;
  /** Timestamp of the last profile update. */
  updated_at: string | null;
  /** Encrypted API keys stored as key-value pairs (provider: encryptedKey). */
  api_keys?: Record<string, string> | null;
}

/** Defines the supported API key providers. */
type ApiKeyProvider = 'openai' | 'anthropic' | 'google' | 'mistral' | 'deepseek' | 'openrouter';

/**
 * Defines the shape of the authentication context provided to consumers.
 */
type AuthContextType = {
  /** The authenticated Supabase user object, or null if not logged in. */
  user: User | null;
  /** The current Supabase session object, or null if not logged in. */
  session: Session | null;
  /** Boolean indicating if the initial session check or a profile fetch is in progress. */
  loading: boolean;
  /** Function to sign in a user with email and password. */
  signIn: (email: string, password: string) => Promise<void>;
  /** Function to sign up a new user. */
  signUp: (email: string, password: string, metadata?: { full_name?: string }) => Promise<void>;
  /** Function to sign out the current user. */
  signOut: () => Promise<void>;
  /** Function to request a password reset email for the user. */
  resetPassword: (email: string) => Promise<void>;
  /** Function to explicitly refresh the user's profile data from the database. */
  refreshProfile: () => Promise<ProfileData | null>;
  /** Function to save an encrypted API key for a specific provider. */
  saveApiKey: (provider: ApiKeyProvider, apiKey: string) => Promise<boolean>;
  /** Function to retrieve and decrypt an API key for a specific provider. */
  getApiKey: (provider: ApiKeyProvider) => Promise<string | null>;
  /** Function to check if an API key exists for a specific provider. */
  hasApiKey: (provider: ApiKeyProvider) => Promise<boolean>;
  /** Function to update user profile information */
  updateProfile: (data: { full_name?: string }) => Promise<boolean>;
  /** Function to update user password */
  updatePassword: (password: string) => Promise<boolean>;
};

/** React context for authentication state and functions. */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Props for the AuthProvider component. */
interface AuthProviderProps {
  children: React.ReactNode;
}

/** Time in milliseconds to cache profile data before refetching (5 minutes). */
const PROFILE_CACHE_TIME = 5 * 60 * 1000;

/**
 * Provides authentication state (user, session) and related functions
 * (signIn, signUp, signOut, resetPassword, profile management, API key management)
 * to its children via context.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loadingInitial, setLoadingInitial] = useState(true); // Separate loading for initial auth check
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [lastProfileFetch, setLastProfileFetch] = useState<number>(0);
  const [isFetchingProfile, setIsFetchingProfile] = useState<boolean>(false); // Flag for concurrent fetches
  const { toast } = useToast();

  // Adding reference to store the current fetch promise to ensure idempotency
  // of concurrent calls to refreshProfile
  const profileFetchPromiseRef = React.useRef<Promise<ProfileData | null> | null>(null);
  // Minimum time between actual profile fetches (in milliseconds)
  const FETCH_DEBOUNCE_TIME = 500;
  // Cache for API key check results to prevent excessive function calls
  const apiKeyCache = React.useRef<
    Record<string, { value: string | boolean | null; timestamp: number }>
  >({});
  // How long to cache API key check results (in milliseconds)
  const API_KEY_CACHE_TIME = 5000; // 5 seconds

  // Effect to handle Supabase auth state changes and initial session loading
  useEffect(() => {
    setLoadingInitial(true);
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Only log significant events to reduce console noise
      // if (event !== 'TOKEN_REFRESHED') {
      //   console.debug(`[AuthProvider] Auth state change: ${event}`);
      // }
      setSession(session);
      const currentUser = session?.user ?? null;
      setUser(currentUser);

      // Update Sentry with user information for error tracking
      if (currentUser) {
        setUserInfo({
          id: currentUser.id,
          email: currentUser.email || undefined,
        });
      } else {
        clearUserInfo();
      }

      if (currentUser?.id !== user?.id) {
        setProfileData(null);
        setLastProfileFetch(0);
        // Clear any pending fetch promise
        profileFetchPromiseRef.current = null;
        // Clear API key cache on user change
        apiKeyCache.current = {};
      }
      // Initial loading is done once we get the first auth state change response or initial session
      setLoadingInitial(false);
    });

    supabase.auth
      .getSession()
      .then(({ data: { session: initialSession } }) => {
        // Keep this log as it's important for initialization debugging
        // console.debug('[AuthProvider] Initial session check');
        if (!session) {
          // Only update if onAuthStateChange hasn't already fired
          setSession(initialSession);
          setUser(initialSession?.user ?? null);
        }
        setLoadingInitial(false);
      })
      .catch((error) => {
        authLogger.error('Error getting initial session', {
          operation: 'getInitialSession',
          error: {
            message: error.message,
            code: error.code,
            status: error.status
          }
        });
        setLoadingInitial(false);
      });

    return () => subscription.unsubscribe();
  }, [user?.id]); // Dependency on user.id is more stable

  // Combined loading state: true if initial check is running OR a profile fetch is in progress
  const loading = loadingInitial || isFetchingProfile;

  /** Signs in a user with email and password. */
  const signIn = useCallback(
    async (email: string, password: string) => {
      try {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } catch (error: any) {
        authLogger.error('Sign in error', {
          operation: 'signIn',
          email: email,
          error: {
            message: error.message,
            code: error.code,
            status: error.status
          }
        });
        toast({ title: 'Error signing in', description: error.message, variant: 'destructive' });
        throw error;
      }
    },
    [toast]
  );

  /** Signs up a new user. */
  const signUp = useCallback(
    async (email: string, password: string, metadata?: { full_name?: string }) => {
      try {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: metadata },
        });
        if (error) throw error;
        toast({
          title: 'Account created',
          description: 'Please check your email to verify your account',
        });
      } catch (error: any) {
        authLogger.error('Sign up error', {
          operation: 'signUp',
          email: email,
          hasMetadata: !!metadata,
          error: {
            message: error.message,
            code: error.code,
            status: error.status
          }
        });
        toast({ title: 'Error signing up', description: error.message, variant: 'destructive' });
        throw error;
      }
    },
    [toast]
  );

  /** Signs out the current user. */
  const signOut = useCallback(async () => {
    try {
      // Clear profile data immediately for faster UI feedback
      setProfileData(null);
      setLastProfileFetch(0);
      profileFetchPromiseRef.current = null;
      // Clear API key cache
      apiKeyCache.current = {};
      // Clear user info in Sentry
      clearUserInfo();
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      // Further cache clearing is handled by onAuthStateChange
    } catch (error: any) {
      authLogger.error('Sign out error', {
        operation: 'signOut',
        userId: user?.id,
        error: {
          message: error.message,
          code: error.code,
          status: error.status
        }
      });
      toast({ title: 'Error signing out', description: error.message, variant: 'destructive' });
      throw error;
    }
  }, [toast]);

  /** Sends a password reset email. */
  const resetPassword = useCallback(
    async (email: string) => {
      try {
        const origin = window.location.origin;
        const redirectTo = `${origin}/auth?reset=true`;
        const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
        if (error) throw error;
        toast({
          title: 'Password reset email sent',
          description: 'Check your email for a link to reset your password',
        });
      } catch (error: any) {
        authLogger.error('Reset password error', {
          operation: 'resetPassword',
          email: email,
          error: {
            message: error.message,
            code: error.code,
            status: error.status
          }
        });
        toast({
          title: 'Error sending reset email',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
    },
    [toast]
  );

  /**
   * Fetches the user profile from the database, updating the cache.
   * Implements debouncing, concurrency control, and returns same promise for concurrent calls.
   */
  const refreshProfile = useCallback(async (): Promise<ProfileData | null> => {
    // Only log the first call in a series of calls to reduce noise
    // const stackTrace = new Error().stack?.split('\n').slice(2, 3).join('\n');
    // console.debug(`[refreshProfile] Called from: ${stackTrace}`);

    // if (!user) {
    //   console.warn("[refreshProfile] Called without a user.");
    //   return null;
    // }

    // If we already have an in-flight fetch promise, return it to ensure idempotent behavior
    if (profileFetchPromiseRef.current) {
      // console.debug("[refreshProfile] Reusing existing fetch promise");
      return profileFetchPromiseRef.current;
    }

    // Check if we're within the debounce window (last fetch was recent)
    const now = Date.now();
    if (lastProfileFetch > 0 && now - lastProfileFetch < FETCH_DEBOUNCE_TIME) {
      // console.debug(`[refreshProfile] Debounced - last fetch was ${now - lastProfileFetch}ms ago`);
      return profileData; // Return current cache state
    }

    // Prevent concurrent fetches
    if (isFetchingProfile) {
      // console.debug("[refreshProfile] Fetch already in progress, skipping.");
      return profileData; // Return current cache state
    }

    // Start a new fetch
    setIsFetchingProfile(true);
    // console.debug("[refreshProfile] Starting new profile fetch");

    // Create the fetch promise and store it for reuse
    const fetchPromise = (async () => {
      try {
        const { data, error, status } = await supabase
          .from('profiles')
          .select('id, username, full_name, avatar_url, updated_at, api_keys')
          .eq('id', user?.id ?? '')
          .single();

        if (error && status !== 406) {
          throw error;
        }

        const fetchedProfile = data as ProfileData | null;
        setProfileData(fetchedProfile);
        setLastProfileFetch(Date.now());
        // Clear API key cache when profile is refreshed
        apiKeyCache.current = {};
        // console.debug("[refreshProfile] Fetch completed successfully");
        return fetchedProfile;
      } catch (error: any) {
        // console.error("[refreshProfile] Error fetching profile:", error);
        toast({
          title: 'Error fetching profile',
          description: error.message,
          variant: 'destructive',
        });
        setProfileData(null);
        setLastProfileFetch(0);
        return null;
      } finally {
        setIsFetchingProfile(false);
        // Clear the promise reference after a small delay to ensure any immediately
        // subsequent calls still reuse this promise
        setTimeout(() => {
          profileFetchPromiseRef.current = null;
        }, 50);
      }
    })();

    // Store the promise so concurrent calls can reuse it
    profileFetchPromiseRef.current = fetchPromise;
    return fetchPromise;
    // Include isFetchingProfile in dependencies to ensure the check is up-to-date
  }, [user, toast, isFetchingProfile]); // Removed profileData dependency

  /** Retrieves profile data, using cache if valid, otherwise fetching (with concurrency prevention). */
  const getProfileData = useCallback(async (): Promise<ProfileData | null> => {
    // Removed the noisy stack trace logging

    const now = Date.now();
    // 1. Use valid cache if available
    if (profileData && now - lastProfileFetch < PROFILE_CACHE_TIME) {
      // console.debug(`[getProfileData] Using cached profile data (${now - lastProfileFetch}ms old)`);
      return profileData;
    }

    // 2. If cache is invalid/missing, but a fetch is already in progress or we have a recent promise, reuse it
    if (profileFetchPromiseRef.current) {
      // console.debug("[getProfileData] Reusing existing fetch promise");
      return profileFetchPromiseRef.current;
    }

    // 3. If cache is invalid/missing and no fetch is in progress, trigger a refresh
    // console.debug("[getProfileData] Cache invalid or missing, triggering refresh");
    return await refreshProfile();
    // Include only essential dependencies
  }, [refreshProfile]); // Removed isFetchingProfile as it's checked inside refreshProfile

  /** Saves an encrypted API key for the user. */
  const saveApiKey = useCallback(
    async (provider: ApiKeyProvider, apiKey: string): Promise<boolean> => {
      if (!user) {
        toast({
          title: 'Not signed in',
          description: 'You must be signed in to save API keys',
          variant: 'destructive',
        });
        return false;
      }

      setIsFetchingProfile(true); // Use the profile fetching flag for loading state
      try {
        // Force refresh profile before saving to ensure we have the latest api_keys
        const currentProfile = await refreshProfile();
        if (!currentProfile)
          throw new Error('Profile not found or could not be loaded before saving API key.');

        const encryptedKey = await encryptApiKey(user.id, apiKey);
        const updatedApiKeys = { ...(currentProfile.api_keys || {}), [provider]: encryptedKey };

        const { error } = await supabase
          .from('profiles')
          .update({ api_keys: updatedApiKeys })
          .eq('id', user.id);

        if (error) throw error;

        // Update local cache immediately
        const updatedProfile = { ...currentProfile, api_keys: updatedApiKeys };
        setProfileData(updatedProfile);
        setLastProfileFetch(Date.now());

        toast({
          title: 'API Key Saved',
          description: `API key for ${provider} saved successfully.`,
        });
        return true;
      } catch (error: any) {
        authLogger.error('Error saving API key', {
          operation: 'saveApiKey',
          provider: provider,
          userId: user?.id,
          error: {
            message: error.message,
            code: error.code,
            status: error.status
          }
        });
        toast({
          title: 'Error Saving API Key',
          description: error.message,
          variant: 'destructive',
        });
        return false;
      } finally {
        setIsFetchingProfile(false);
      }
      // refreshProfile is now called inside, so getProfileData isn't needed in deps here
    },
    [user, toast, refreshProfile]
  );

  /** Retrieves and decrypts an API key for the user. */
  const getApiKey = useCallback(
    async (provider: ApiKeyProvider): Promise<string | null> => {
      if (!user) return null;

      // Check if we have a cached result for this provider
      const cacheKey = `getApiKey:${provider}`;
      const now = Date.now();
      const cachedResult = apiKeyCache.current[cacheKey];

      if (cachedResult && now - cachedResult.timestamp < API_KEY_CACHE_TIME) {
        // Use cached result if it's still valid
        return cachedResult.value as string | null;
      }

      try {
        // Use getProfileData which handles caching and concurrency
        const profile = await getProfileData();
        const encryptedKey = profile?.api_keys?.[provider];

        if (!encryptedKey) {
          // Cache the negative result
          apiKeyCache.current[cacheKey] = { value: null, timestamp: now };
          return null;
        }

        // Decrypt and cache the result
        const decryptedKey = await decryptApiKey(user.id, encryptedKey);
        apiKeyCache.current[cacheKey] = { value: decryptedKey, timestamp: now };
        return decryptedKey;
      } catch (error: any) {
        authLogger.error('Error getting/decrypting API key', {
          operation: 'getApiKey',
          provider: provider,
          userId: user?.id,
          error: {
            message: error.message,
            code: error.code,
            status: error.status
          }
        });
        // Cache the error result
        apiKeyCache.current[cacheKey] = { value: null, timestamp: now };
        return null;
      }
    },
    [user, getProfileData]
  );

  /** Checks if an API key exists for the given provider. */
  const hasApiKey = useCallback(
    async (provider: ApiKeyProvider): Promise<boolean> => {
      // Check if we have a cached result for this provider
      const cacheKey = `hasApiKey:${provider}`;
      const now = Date.now();
      const cachedResult = apiKeyCache.current[cacheKey];

      if (cachedResult && now - cachedResult.timestamp < API_KEY_CACHE_TIME) {
        // Use cached result if it's still valid
        return cachedResult.value as boolean;
      }

      try {
        // Use getProfileData which handles caching and concurrency
        const profile = await getProfileData();
        const hasKey = !!profile?.api_keys?.[provider];

        // Cache the result
        apiKeyCache.current[cacheKey] = { value: hasKey, timestamp: now };
        return hasKey;
      } catch (error: any) {
        authLogger.error('Error checking API key', {
          operation: 'hasApiKey',
          provider: provider,
          userId: user?.id,
          error: {
            message: error.message,
            code: error.code,
            status: error.status
          }
        });
        // Cache the error result
        apiKeyCache.current[cacheKey] = { value: false, timestamp: now };
        return false;
      }
    },
    [getProfileData]
  );

  /** Updates the user's profile information */
  const updateProfile = useCallback(
    async (data: { full_name?: string }): Promise<boolean> => {
      try {
        if (!user) {
          toast({
            title: 'Error',
            description: 'You must be logged in to update your profile',
            variant: 'destructive',
          });
          return false;
        }

        // Update auth metadata
        const { error: authError } = await supabase.auth.updateUser({
          data: { full_name: data.full_name },
        });

        if (authError) throw authError;

        // Update profiles table
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ full_name: data.full_name })
          .eq('id', user.id);

        if (profileError) throw profileError;

        // Refresh profile data
        await refreshProfile();

        toast({
          title: 'Profile updated',
          description: 'Your profile information has been updated',
        });
        return true;
      } catch (error: any) {
        authLogger.error('Update profile error', {
          operation: 'updateProfile',
          userId: user?.id,
          updateData: data,
          error: {
            message: error.message,
            code: error.code,
            status: error.status
          }
        });
        toast({
          title: 'Error updating profile',
          description: error.message,
          variant: 'destructive',
        });
        return false;
      }
    },
    [user, toast, refreshProfile]
  );

  /** Updates the user's password */
  const updatePassword = useCallback(
    async (password: string): Promise<boolean> => {
      try {
        if (!user) {
          toast({
            title: 'Error',
            description: 'You must be logged in to update your password',
            variant: 'destructive',
          });
          return false;
        }

        const { error } = await supabase.auth.updateUser({ password });

        if (error) throw error;

        toast({
          title: 'Password updated',
          description: 'Your password has been successfully updated',
        });
        return true;
      } catch (error: any) {
        authLogger.error('Update password error', {
          operation: 'updatePassword',
          userId: user?.id,
          error: {
            message: error.message,
            code: error.code,
            status: error.status
          }
        });
        toast({
          title: 'Error updating password',
          description: error.message,
          variant: 'destructive',
        });
        return false;
      }
    },
    [user, toast]
  );

  // Memoize the context value
  const value = useMemo(
    () => ({
      user,
      session,
      loading,
      signIn,
      signUp,
      signOut,
      resetPassword,
      refreshProfile,
      saveApiKey,
      getApiKey,
      hasApiKey,
      updateProfile,
      updatePassword,
    }),
    [
      user,
      session,
      loading,
      signIn,
      signUp,
      signOut,
      resetPassword,
      refreshProfile,
      saveApiKey,
      getApiKey,
      hasApiKey,
      updateProfile,
      updatePassword,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Custom hook to access the authentication context.
 * Must be used within an `AuthProvider`.
 *
 * @returns {AuthContextType} The authentication context.
 * @throws {Error} If used outside of an `AuthProvider`.
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
